import { useState, type DragEvent } from 'react'
import { moveEntry, type ToyIndex } from '../upload/toyIndex'
import { str } from '../types/toyEntry'
import './ToyIndexOrder.css'

type Props = {
  entries: ToyIndex
  showPublishHint: boolean
  disabled: boolean
  saving: boolean
  onSave: (reordered: ToyIndex) => void
}

// Drag-and-drop reordering of toy-index.json entries. The reorder is purely
// in-memory until "Save updated index"; the parent owns the S3 write and
// remounts this component (key on the index ETag) after each reload, which is
// what resets the working order — there is deliberately no sync effect.
export default function ToyIndexOrder({
  entries,
  showPublishHint,
  disabled,
  saving,
  onSave,
}: Props) {
  const [order, setOrder] = useState(() => entries)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  if (entries.length === 0) return null

  // Entries are never cloned, so reference equality detects any move.
  const dirty = order.some((e, i) => e !== entries[i])

  const handleDragStart = (index: number) => (e: DragEvent<HTMLLIElement>) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(index))
    setDraggingIndex(index)
  }

  const handleDragOver = (index: number) => (e: DragEvent<HTMLLIElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverIndex !== index) setDragOverIndex(index)
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (targetIndex: number) => (e: DragEvent<HTMLLIElement>) => {
    e.preventDefault()
    setDraggingIndex(null)
    setDragOverIndex(null)
    const sourceIndex = Number.parseInt(e.dataTransfer.getData('text/plain'), 10)
    if (!Number.isFinite(sourceIndex)) return
    if (sourceIndex === targetIndex) return
    setOrder((current) => moveEntry(current, sourceIndex, targetIndex))
  }

  const handleDragEnd = () => {
    setDraggingIndex(null)
    setDragOverIndex(null)
  }

  return (
    <section className="toy-index-order">
      <h2 className="toy-index-order__heading">toy-index.json order</h2>
      <ol className="toy-index-order__list" aria-label="Toy index order">
        {order.map((entry, index) => {
          const id = str(entry.id)
          const title = str(entry.title) ?? id ?? `entry ${index + 1}`
          const thumbnailUrl = str(entry.thumbnailUrl)
          const classes = [
            'toy-index-order__item',
            draggingIndex === index ? 'toy-index-order__item--dragging' : '',
            dragOverIndex === index && draggingIndex !== index
              ? 'toy-index-order__item--dragover'
              : '',
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <li
              key={id ?? index}
              className={classes}
              title={title}
              draggable
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              <span
                className="toy-index-order__handle"
                aria-hidden="true"
                title="Drag to reorder"
              >
                ⋮⋮
              </span>
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={title}
                  className="toy-index-order__thumbnail"
                  draggable={false}
                />
              ) : (
                <div className="toy-index-order__art" aria-hidden="true">
                  {title.charAt(0)}
                </div>
              )}
              <span className="toy-index-order__caption">{title}</span>
            </li>
          )
        })}
      </ol>
      <div className="toy-index-order__footer">
        <button
          type="button"
          onClick={() => onSave(order)}
          disabled={disabled || saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save updated index'}
        </button>
        {dirty && !saving && (
          <button
            type="button"
            className="toy-index-order__reset"
            onClick={() => setOrder(entries)}
            disabled={disabled}
          >
            Reset
          </button>
        )}
        {dirty && showPublishHint && (
          <span className="admin-user__status-note">
            Saving writes toy-index.json to S3. Run get_toy_index.sh, commit, and redeploy to
            publish.
          </span>
        )}
      </div>
    </section>
  )
}
